import { Link } from "react-router-dom";
import { UtensilsCrossed, Leaf, CalendarCheck, Clock, MapPin, AlertCircle, Heart, Gem, Cake, Baby, Briefcase, Users, Phone, Mail, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Zellige } from "../components/Zellige";
import { site } from "../config/site";
import { signatureDishes } from "../config/menu";
import { methode } from "../config/traiteur";

// TODO: remplacer les images Unsplash par de vraies photos TN FOOD (avec l'accord du client)
const HERO_IMG = "https://images.unsplash.com/photo-1651440204216-548382747b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const RESTO_IMG = "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const TRAITEUR_IMG = "https://images.unsplash.com/photo-1555244162-803834f70033?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const reassurance = [
  { icon: UtensilsCrossed, label: "Cuisine tunisienne authentique" },
  { icon: Leaf, label: "Ingrédients frais" },
  { icon: Clock, label: "Devis sous 48 h ouvrées" },
  { icon: AlertCircle, label: "Allergènes sur demande" },
  { icon: MapPin, label: "Traiteur : toute la France" },
];

const eventIcons = { Heart, Gem, Cake, Baby, Briefcase, Users };
const homeEvents = [
  { icon: "Heart", label: "Mariages" },
  { icon: "Gem", label: "Fiançailles" },
  { icon: "Cake", label: "Anniversaires" },
  { icon: "Baby", label: "Baptêmes" },
  { icon: "Briefcase", label: "Entreprise" },
  { icon: "Users", label: "Fêtes familiales" },
];

export default function Home() {
  return (
    <main id="contenu-principal">
      <Seo
        title="TN FOOD — Restaurant tunisien & traiteur événementiel à Sainte-Savine près de Troyes"
        description="Cuisine tunisienne authentique sur place ou à emporter à Sainte-Savine, et traiteur pour mariages, anniversaires et événements d'entreprise partout en France. Devis sous 48 h."
        path="/"
      />

      {/* 1. Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="home-hero">
        {/* TODO: remplacer par une vraie photo (plat tunisien ou table garnie) */}
        <img src={HERO_IMG} alt="Table garnie de plats tunisiens au restaurant TN FOOD" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-24">
          <Reveal>
            <p className="text-gold uppercase tracking-[0.3em] text-sm font-semibold mb-4">Restaurant · Traiteur événementiel</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-cream max-w-3xl leading-tight">
              TN FOOD — Cuisine tunisienne authentique &amp; service traiteur pour vos événements
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cream/90 max-w-2xl leading-relaxed">
              À Sainte-Savine, près de Troyes, savourez les saveurs de la Méditerranée et confiez-nous l'organisation culinaire de vos plus beaux moments.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact#reservation"
                data-testid="hero-cta-reserver"
                className="rounded-full bg-terracotta text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-terracotta-dark hover:shadow-lg hover:-translate-y-0.5"
              >
                Réserver une table
              </Link>
              <Link
                to="/contact#devis"
                data-testid="hero-cta-devis"
                className="rounded-full bg-cream text-encre px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-gold hover:shadow-lg hover:-translate-y-0.5"
              >
                Demander un devis traiteur
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. Bandeau de réassurance */}
      <section className="bg-nuit text-cream py-8" data-testid="home-reassurance">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl flex flex-wrap justify-center gap-x-10 gap-y-4">
          {reassurance.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 text-sm">
              <Icon className="h-5 w-5 text-gold" strokeWidth={1.5} aria-hidden="true" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Double entrée */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="home-double-entree">
        <Zellige />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl grid md:grid-cols-2 gap-8">
          <Reveal>
            <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-terracotta/10 h-full flex flex-col">
              {/* TODO: remplacer par une vraie photo du restaurant */}
              <img src={RESTO_IMG} alt="Plat tunisien servi au restaurant TN FOOD" className="h-56 w-full object-cover" loading="lazy" />
              <div className="p-8 flex flex-col flex-1">
                <h2 className="font-display text-3xl font-semibold text-encre">Le Restaurant</h2>
                <p className="mt-3 text-taupe leading-relaxed flex-1">
                  Couscous, tajines, briks et grillades préparés sur place, dans un cadre chaleureux de 40 couverts avec terrasse selon la saison. Sur place, à emporter ou en livraison dans l'agglomération troyenne.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/carte" data-testid="card-resto-carte" className="rounded-full bg-terracotta text-cream px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-terracotta-dark">Voir la carte</Link>
                  <Link to="/contact#reservation" data-testid="card-resto-reserver" className="rounded-full border-2 border-terracotta text-terracotta px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-terracotta hover:text-cream">Réserver</Link>
                </div>
              </div>
            </article>
          </Reveal>
          <Reveal delay={0.15}>
            <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-terracotta/10 h-full flex flex-col">
              {/* TODO: remplacer par une vraie photo d'événement traiteur */}
              <img src={TRAITEUR_IMG} alt="Buffet traiteur TN FOOD lors d'un mariage" className="h-56 w-full object-cover" loading="lazy" />
              <div className="p-8 flex flex-col flex-1">
                <h2 className="font-display text-3xl font-semibold text-encre">Le Traiteur</h2>
                <p className="mt-3 text-taupe leading-relaxed flex-1">
                  Mariages, fiançailles, anniversaires, baptêmes, événements d'entreprise : buffets, cocktails dînatoires, service à table et livraison dans toute la France.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/traiteur" data-testid="card-traiteur-prestations" className="rounded-full bg-saffron text-cream px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-saffron-dark">Voir les prestations</Link>
                  <Link to="/contact#devis" data-testid="card-traiteur-devis" className="rounded-full border-2 border-saffron text-saffron px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-saffron hover:text-cream">Demander un devis</Link>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      {/* 4. Présentation courte */}
      <section className="bg-cream py-16 md:py-24" data-testid="home-presentation">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Bienvenue chez TN FOOD</h2>
            <div className="mt-6 space-y-4 text-taupe leading-relaxed text-lg">
              <p>
                TN FOOD est une SAS au capital de 1 000 €, immatriculée au RCS de Troyes et présidée par Monsieur NEJI Ali. Notre restaurant vous accueille à Sainte-Savine, à quelques minutes de Troyes.
              </p>
              <p>
                Nous y préparons des spécialités tunisiennes traditionnelles, sur place, avec des produits frais et des épices authentiques : couscous, tajines, briks, salades tunisiennes, grillades et pâtisseries orientales.
              </p>
              <p>
                Notre service traiteur accompagne vos mariages, fiançailles, anniversaires, baptêmes, événements d'entreprise et fêtes familiales, partout en France.
              </p>
              <p>
                Générosité, convivialité, respect des traditions et personnalisation : voilà ce qui guide chacune de nos assiettes et chacune de nos prestations.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5. Aperçu de la carte */}
      <section className="py-16 md:py-24" data-testid="home-apercu-carte">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-terracotta uppercase tracking-[0.25em] text-sm font-semibold">Notre carte</p>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre mt-2">Nos plats signature</h2>
            </div>
            <Link to="/carte" data-testid="apercu-carte-lien" className="inline-flex items-center gap-2 text-terracotta font-semibold hover:gap-3 transition-all">
              Voir la carte complète <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {signatureDishes.map((dish, i) => (
              <Reveal key={dish.name} delay={i * 0.1}>
                <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-terracotta/10">
                  <img src={dish.image} alt={dish.name} className="h-44 w-full object-cover" loading="lazy" width="800" height="600" />
                  <div className="p-5">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="font-display text-lg font-semibold text-encre">{dish.name}</h3>
                      <span className="text-terracotta font-bold whitespace-nowrap">{dish.price}</span>
                    </div>
                    <p className="mt-2 text-sm text-taupe leading-relaxed">{dish.description}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Aperçu traiteur */}
      <section className="bg-bordeaux text-cream py-16 md:py-24" data-testid="home-apercu-traiteur">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-gold uppercase tracking-[0.25em] text-sm font-semibold">Traiteur événementiel</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold mt-2">Tous vos événements méritent une table généreuse</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {homeEvents.map((ev, i) => {
              const Icon = eventIcons[ev.icon];
              return (
                <Reveal key={ev.label} delay={i * 0.08} className="text-center">
                  <div className="mx-auto h-16 w-16 rounded-full bg-cream/10 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-gold" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <p className="mt-3 font-medium">{ev.label}</p>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="text-center mt-12">
            <Link to="/traiteur" data-testid="apercu-traiteur-lien" className="inline-block rounded-full bg-saffron text-cream px-8 py-3 font-semibold transition-all duration-300 hover:bg-gold hover:-translate-y-0.5">
              Découvrir nos prestations traiteur
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 7. Méthode traiteur en 4 étapes */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="home-methode">
        <Zellige className="text-saffron" />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Votre événement en 4 étapes</h2>
          </Reveal>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methode.map((m, i) => (
              <Reveal key={m.step} delay={i * 0.1}>
                <li className="relative bg-white rounded-2xl p-6 border border-terracotta/10 shadow-sm h-full">
                  <span className="font-display text-5xl font-bold text-terracotta/20">{m.step}</span>
                  <h3 className="font-display text-xl font-semibold text-encre mt-2">{m.title}</h3>
                  <p className="mt-2 text-sm text-taupe leading-relaxed">{m.description}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* 8. Bloc « Avis clients » — désactivé volontairement.
             Ne pas inventer de témoignages. Insérer ici de vrais avis
             (Google, etc.) lorsqu'ils seront disponibles, avec lien source. */}

      {/* 9. CTA final */}
      <section className="bg-nuit text-cream py-16 md:py-24" data-testid="home-cta-final">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-semibold">Un repas en famille, un événement à organiser ? Parlons-en.</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/contact#reservation" data-testid="cta-final-reserver" className="rounded-full bg-terracotta text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5">
                Réserver
              </Link>
              <Link to="/contact#devis" data-testid="cta-final-devis" className="rounded-full bg-cream text-encre px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-gold hover:-translate-y-0.5">
                Devis traiteur
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-cream/85">
              <a href={site.phoneHref} data-testid="cta-final-phone" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> {site.phone}
              </a>
              <a href={`mailto:${site.email}`} data-testid="cta-final-email" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="h-4 w-4" strokeWidth={1.5} /> {site.email}
              </a>
              <span className="inline-flex items-center gap-2">
                <CalendarCheck className="h-4 w-4" strokeWidth={1.5} /> Devis traiteur sous 48 h ouvrées
              </span>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
